import React, { useState } from 'react';

import { Box, Typography, useTheme, Button } from '@mui/material';
import { CustomAvatar, FlexyDate } from '@europrocurement/flexy-components';

import {
    GroupesDeTravailJsonld as GroupeTravailDTO,
    JointureUtilisateurGroupeTravailJsonldUserworkgroupRead as Inscriptions,
    GroupesDeTravailInscriptionsInner as InscriptionDTO,
} from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';

import { alertCloseIcon, FaOptionIcon, updateIcon } from '@europrocurement/l2d-icons';
import {
    GroupeTravailUtilisateurApiObject,
    useUtilisateurService,
    UtilisateursAppDispatch,
} from '@europrocurement/l2d-domain';

import { useDispatch } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { format } from 'date-fns';

// Type
export type EditMembersWidgetProps = {
    member: Inscriptions;
    workgroup: GroupeTravailUtilisateurApiObject;
    historicInscription: boolean;
};

// Component
export const EditMembersWidget: React.FunctionComponent<EditMembersWidgetProps> = function ({
    member,
    workgroup,
    historicInscription,
}) {
    const theme = useTheme();
    const dispatch = useDispatch<UtilisateursAppDispatch>();

    // Api
    const { groupesTravailApi, selectGroupeTravail } = useUtilisateurService();

    // Init state for calendars
    const currentDebutDate: Date | null = member.dDebut ? new Date(member.dDebut) : null;
    const currentEndDate: Date | null = member.dFin ? new Date(member.dFin) : null;
    const [newDebutDate, setNewDebutDate] = useState<Date | null>(currentDebutDate);
    const [newEndDate, setNewEndDate] = useState<Date | null>(currentEndDate);

    // Toggle edit options pannel state
    const [displayDate, setDisplayDate] = useState<boolean>(false);
    const [displayModifyValidation, setDisplayModifyValidation] = useState<boolean>(false);
    const [displayDeleteValidation, setDisplayDeleteValidation] = useState<boolean>(false);

    const developWidget = () => {
        setDisplayDate(!displayDate);
    };

    // Allow date to be null (FlexyDate don't)
    const setNewEndDateState = (entry: Date | null) => {
        if (entry !== null) {
            setNewEndDate(entry);
        } else {
            setNewEndDate(null);
        }
    };

    // Prepare base DTO structure without inscriptions
    const setWorkGroupDTO = () => {
        if (!workgroup) {
            enqueueSnackbar('Une erreur est survenue: groupe de travail non trouvé', {
                variant: 'error',
            });
            throw new Error('Une erreur est survenue: groupe de travail non trouvé');
        }

        // Format workgroup DTO.
        const formatWorkgroup: GroupeTravailDTO = {
            idGroupeTravail: workgroup['@id'],
            lib: workgroup.lib,
            domaine: workgroup.domaine?.['@id'],
            iduserReferent: workgroup.iduserReferent?.['@id'],
            fDelete: workgroup.fDelete,
            phone: workgroup.phone,
            email: workgroup.email,
            dCreation: workgroup.dCreation,
            dCloture: workgroup.dCloture,
            inscriptions: [],
        };

        return formatWorkgroup;
    };

    /**
     * Update submission part
     *
     * UPDATE ACTIVE
     * UPDATE INACTIVE
     * DELETE
     */
    const handleModify = (needDeletion?: boolean): void => {
        // Check dates consistancy
        if (historicInscription || needDeletion) {
            if (newEndDate === null) {
                enqueueSnackbar(
                    'Une inscription inactive ou a supprimé doit toujours avoir une date de fin',
                    {
                        variant: 'error',
                    },
                );
                throw new Error('Une inscription inactive doit toujours avoir une date de fin');
            }
        }
        if (newDebutDate === null) {
            enqueueSnackbar('Une inscription doit toujours avoir une date de début', {
                variant: 'error',
            });
            throw new Error('Une inscription doit toujours avoir une date de début');
        }
        if (newEndDate === null && workgroup.dCloture) {
            enqueueSnackbar(
                'Une inscription doit toujours avoir une date de fin quand son groupe a une date de clôture',
                {
                    variant: 'error',
                },
            );
            throw new Error(
                'Une inscription doit toujours avoir une date de fin quand son groupe a une date de clôture',
            );
        }
        if (newDebutDate && newEndDate && newDebutDate > newEndDate) {
            // Handle bug with time differencies due to misconversion
            if (newDebutDate.toLocaleDateString() !== newEndDate.toLocaleDateString()) {
                enqueueSnackbar('La date de début ne peut être postérieure à la date de fin', {
                    variant: 'error',
                });
                throw new Error('La date de début ne peut être postérieure à la date de fin');
            }
        }
        if (newDebutDate && workgroup.dCreation && newDebutDate < new Date(workgroup.dCreation)) {
            // Handle bug with time differencies due to misconversion
            if (
                newDebutDate.toLocaleDateString() !==
                new Date(workgroup.dCreation).toLocaleDateString()
            ) {
                enqueueSnackbar(
                    'La date de début ne peut être antérieure à la date de création du groupe',
                    {
                        variant: 'error',
                    },
                );
                throw new Error(
                    'La date de début ne peut être antérieure à la date de création du groupe',
                );
            }
        }
        if (newEndDate && workgroup.dCloture && newEndDate > new Date(workgroup.dCloture)) {
            enqueueSnackbar(
                'La date de fin ne peut être postérieure à la date de cloture du groupe',
                {
                    variant: 'error',
                },
            );
            throw new Error(
                'La date de fin ne peut être postérieure à la date de cloture du groupe',
            );
        }

        const updatedWorkgroup = setWorkGroupDTO();

        const updateInscr: InscriptionDTO = {
            idUserGroupeTravail: member.id,
            user: member.user?.['@id'] as string,
            groupetravail: member.groupetravail?.['@id'] as string,
            // If active and need deletion, set fdelete to true
            fDelete: !historicInscription && needDeletion ? true : member.fDelete,
            dDebut:
                newDebutDate !== null
                    ? format(newDebutDate, 'yyyy-MM-dd')
                    : (member.dDebut ?? null),
            dFin: newEndDate !== null ? format(newEndDate, 'yyyy-MM-dd') : null,
        };

        const updateWorkGroup = () => {
            groupesTravailApi
                .apiGroupestravailIdGroupeTravailStatusPatch({
                    idGroupeTravail: `${workgroup.id}`,
                    status: 'none',
                    groupesDeTravail: updatedWorkgroup as GroupeTravailDTO,
                })
                .then(() => {
                    // Add notification to user.
                    enqueueSnackbar('Le membre a bien été modifié.', {
                        variant: 'success',
                    });

                    // Close toggler
                    setDisplayModifyValidation(false);

                    // Update store.
                    dispatch(selectGroupeTravail({ id: workgroup.id as number }));
                })
                .catch((error) => {
                    console.error(error);
                    const errorMessage = error.response?.data?.exception?.message;
                    enqueueSnackbar(`Une erreur est survenue: ${errorMessage}`, {
                        variant: 'error',
                    });
                });
        };

        if (updatedWorkgroup) {
            updatedWorkgroup.inscriptions = [updateInscr];
            console.log(updatedWorkgroup.inscriptions);
            updateWorkGroup();
        } else {
            enqueueSnackbar('Une erreur est survenue: groupe de travail non trouvé', {
                variant: 'error',
            });
        }
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="start"
            sx={{
                margin: '5px 0px 0px 0px',
                padding: '12px',
                backgroundColor: theme.palette.background.default,
                borderRadius: '8px',
            }}
        >
            {/* Base container */}
            <Box
                display="flex"
                alignItems="center"
                justifyContent="start"
                width="100%"
            >
                {/* Avatar container */}
                <Box
                    sx={{
                        marginRight: '20px',
                    }}
                >
                    <CustomAvatar
                        firstName={member.user?.prenom}
                        lastName={member.user?.nom}
                        keycloakId={member.user?.userIdentifier || undefined}
                        sx={{ width: 50, height: 50, borderRadius: '50px' }}
                    />
                </Box>

                {/* Name + Mail container */}
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="start"
                    sx={{
                        marginRight: '10px',
                        width: '80%',
                    }}
                >
                    <Typography
                        variant="l2dh7"
                        sx={{ lineHeight: '1.4' }}
                    >
                        {member.user?.prenom} {member.user?.nom}
                    </Typography>

                    <Typography
                        variant="l2dbodyxs"
                        color="text.gris50"
                    >
                        {member.user?.mail}
                    </Typography>
                </Box>

                {/* Dropdown icon container */}
                <Box>
                    <Typography
                        variant="l2dbodyxs"
                        color="text.gris50"
                    >
                        <Button
                            aria-label="toggle-edit-options"
                            onClick={() => {
                                developWidget();
                            }}
                        >
                            {displayDate ? (
                                <FaOptionIcon
                                    {...alertCloseIcon.props}
                                    sx={{
                                        fontSize: 18,
                                        color: theme.palette.grey[600],
                                    }}
                                />
                            ) : (
                                <FaOptionIcon
                                    {...updateIcon.props}
                                    sx={{
                                        fontSize: 18,
                                        color: theme.palette.grey[600],
                                    }}
                                />
                            )}
                        </Button>
                    </Typography>
                </Box>
            </Box>

            {/* Extended container */}
            {displayDate && (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    width="100%"
                    paddingTop="17px"
                >
                    {/* Inscription (start) */}
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        width="100%"
                        margin="5px 0"
                    >
                        <Typography
                            variant="l2dbodys"
                            width="50%"
                            color="text.gris70"
                        >
                            Membre depuis le :
                        </Typography>

                        <FlexyDate
                            aria-label="debut-date-input-form"
                            size="small"
                            value={newDebutDate}
                            onChange={setNewDebutDate}
                        />
                    </Box>

                    {/* Inscription (end) */}
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        width="100%"
                        margin="5px 0"
                    >
                        <Typography
                            variant="l2dbodys"
                            width="50%"
                            color="text.gris70"
                        >
                            Membre jusqu&apos;au :
                        </Typography>

                        <FlexyDate
                            aria-label="end-date-input-form"
                            size="small"
                            value={newEndDate}
                            onChange={setNewEndDateState}
                        />
                    </Box>

                    {/* Button Container */}
                    {!displayModifyValidation && !displayDeleteValidation ? (
                        <Box
                            display="flex"
                            flexDirection="column"
                            width="100%"
                            paddingTop="5px"
                        >
                            <Typography
                                variant="l2dbodys"
                                color="text.gris70"
                            >
                                {!historicInscription
                                    ? 'Valider la modification ou la suppression :'
                                    : 'Valider la modification :'}
                            </Typography>

                            <Box
                                display="flex"
                                flexDirection="row"
                                justifyContent="space-around"
                                width="100%"
                                paddingTop="10px"
                            >
                                <Button
                                    aria-label="Inscription modification button"
                                    type="button"
                                    variant="contained"
                                    color="success"
                                    onClick={() => setDisplayModifyValidation(true)}
                                >
                                    Modifier
                                </Button>
                                {!historicInscription && (
                                    <Button
                                        aria-label="Inscription delete button"
                                        type="button"
                                        variant="contained"
                                        color="error"
                                        onClick={() => setDisplayDeleteValidation(true)}
                                    >
                                        Supprimer
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            display="flex"
                            flexDirection="column"
                            width="100%"
                        >
                            <Typography
                                variant="l2dbodys"
                                color="text.gris70"
                            >
                                Êtes vous sûr ?
                            </Typography>

                            <Box
                                display="flex"
                                flexDirection="row"
                                justifyContent="space-around"
                                width="100%"
                                paddingTop="15px"
                            >
                                {displayModifyValidation && (
                                    <Button
                                        aria-label="Validate inscription modification button"
                                        type="button"
                                        variant="contained"
                                        color="success"
                                        onClick={() => handleModify()}
                                    >
                                        Confirmer
                                    </Button>
                                )}

                                {displayDeleteValidation && (
                                    <Button
                                        aria-label="Validate inscription deletion button"
                                        type="button"
                                        variant="contained"
                                        color="error"
                                        onClick={() => handleModify(true)}
                                    >
                                        Confirmer
                                    </Button>
                                )}

                                <Button
                                    aria-label="Cancel inscription modification or deletion button"
                                    type="button"
                                    variant="contained"
                                    onClick={() => {
                                        setDisplayModifyValidation(false);
                                        setDisplayDeleteValidation(false);
                                    }}
                                >
                                    Annuler
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};
